import * as React from "react";
import { createPortal } from "react-dom";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { IPackInfo } from "reducers/collection";
import { ColDef } from "ag-grid-community";
import { formatMessage } from "lib/globalize-util";
import { yesNoValueFormatter } from "components/allocation-dimension/yes-no-cell-renderer";
import productCollectionGridViewStyles from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-styles";
import { AgGridReact } from "ag-grid-react";
import { COLUMN_DEF } from "components/grid";
import { OrderedSet } from "immutable";
import { debounce } from "@material-ui/core";
import { DATATEST } from "constants/integration";

type ColDefOverrides = {
    [key in SizePacksDataColumns | SizePacksCalculatedColumns]?: Partial<ColDef>;
};
interface IProps {
    sizes: { [key: string]: IPackInfo } | IPackInfo[];
    sizeLayout?: SizeLayout;
    autoColumns: (SizePacksDataColumns | SizePacksCalculatedColumns)[];
    noRowsComp?: React.ReactNode;
    colDefOverrides?: ColDefOverrides;
    onFetchMoreSizePacks?: (lastId: string) => void;
}

export enum SizeLayout {
    AsCells = "as_cells",
    AsColumns = "as_columns",
}

export enum SizePacksDataColumns {
    PackName = "name",
    PackId = "id",
    PackUPC = "packUPC",
    PackType = "packType",
    Archived = "active",
    BrandId = "brandIdentifier",
}

export enum SizePacksCalculatedColumns {
    UnitsPerPack = "units_per_pack",
}

const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 28;
const getValueFormatter = (name: string) =>
    ({ [SizePacksDataColumns.Archived]: ({ value }) => yesNoValueFormatter(!value) })[name];
const getAdditionalAttributes = (name: string) => ({ [SizePacksDataColumns.PackName]: { pinned: "left" } })[name] ?? {};
const getValueGetter = (name) =>
    ({
        [SizePacksCalculatedColumns.UnitsPerPack]: ({ data }: { data: IPackInfo }) =>
            data.packUnits.reduce((acc, curr) => acc + curr.units, 0),
    })[name];
const getHeaderName = (name: string) =>
    ({
        [SizePacksDataColumns.BrandId]: "Brand Identifier",
        [SizePacksDataColumns.PackType]: "Pack Type",
        [SizePacksDataColumns.PackUPC]: "Case Pack UPC",
        [SizePacksCalculatedColumns.UnitsPerPack]: "Units per Pack / Multiple",
        [SizePacksDataColumns.PackId]: "NuORDER Pack ID",
        [SizePacksDataColumns.Archived]: "Archived",
        [SizePacksDataColumns.PackName]: "Name",
    })[name] ?? `${name.replace(name[0], name[0].toUpperCase())}`;

const createAutoColumnByName = (name: string): ColDef => ({
    field: name,
    colId: name,
    headerName: formatMessage(getHeaderName(name)),
    valueFormatter: getValueFormatter(name),
    valueGetter: getValueGetter(name),
    ...getAdditionalAttributes(name),
    sortable: true,
});

export const SizePacksTable: React.FC<IProps> = ({
    sizes,
    sizeLayout = SizeLayout.AsCells,
    autoColumns,
    noRowsComp,
    colDefOverrides,
    onFetchMoreSizePacks,
}) => {
    // As we have a Size_Name|Size_Quantity schema for each size, the number of sizes is half the number of columns.
    // Size and quantities are located on the same object (hence, at the same index), so here we map n and n+1 to the same index,
    // and differentiate the key we must access depending on the parity of n
    const sizesAsCellsGetterCreator =
        (n) =>
        ({ data }: { data: IPackInfo }) =>
            data.packUnits[(n / 2) >>> 0]?.[n % 2 ? "units" : "normalized_key"];
    const sizesAsColumnsGetterCreator =
        (size) =>
        ({ data }: { data: IPackInfo }) =>
            data.packUnits.find((pack) => pack.normalized_key === size)?.units;

    const containerRef = React.useRef<HTMLDivElement>();

    const columnDef: ColDef[] = React.useMemo(() => {
        const autoColumnsDefs = autoColumns.map((name) => {
            const colDef = createAutoColumnByName(name);
            return colDefOverrides?.[name] ? { ...colDef, ...colDefOverrides[name] } : colDef;
        });

        if (!sizes) {
            return autoColumnsDefs;
        }

        const sizeColumns: ColDef[] = [];
        const correctedSizes = Array.isArray(sizes) ? sizes : sizes && Object.values(sizes);
        if (sizeLayout === SizeLayout.AsCells) {
            const maxNumberOfSizes = Object.values(sizes).reduce(
                (acc, curr) => Math.max(acc, curr.packUnits.length),
                0
            );
            Array(2 * maxNumberOfSizes)
                .fill(1)
                .forEach((_, i) =>
                    sizeColumns.push({
                        headerName: formatMessage(i % 2 ? "Qty |sizeNumber|" : "Size |sizeNumber|", {
                            sizeNumber: Math.floor(i / 2) + 1,
                        }),
                        valueGetter: sizesAsCellsGetterCreator(i),
                        sortable: true,
                    })
                );
        } else if (sizeLayout === SizeLayout.AsColumns) {
            const uniqueSizes = OrderedSet<string>(
                correctedSizes
                    .map((size) => size.packUnits.map((packInfo) => packInfo.normalized_key))
                    .reduce((acc, curr) => acc.concat(curr), [])
            );
            uniqueSizes.forEach((size) =>
                sizeColumns.push({ headerName: size, valueGetter: sizesAsColumnsGetterCreator(size) })
            );
        }
        return autoColumnsDefs.concat(sizeColumns);
    }, [sizeLayout, autoColumns, sizes]); // eslint-disable-line react-hooks/exhaustive-deps

    const rowData = React.useMemo(() => (Array.isArray(sizes) ? sizes : sizes && Object.values(sizes)) ?? [], [sizes]);
    const noRowsNode = noRowsComp && document.getElementsByClassName("no-rows-node")?.[0];

    const debouncedOnFetchMoreSizePacks = React.useMemo(
        () => onFetchMoreSizePacks && debounce(onFetchMoreSizePacks),
        [onFetchMoreSizePacks]
    );
    const handleBodyScroll = React.useCallback(
        ({ direction, top }) => {
            if (direction === "vertical") {
                const totalScrollHeight = rowData.length * ROW_HEIGHT;
                const lastDataPiece = rowData[rowData.length - 1];
                if (containerRef.current.scrollHeight - HEADER_HEIGHT + top + ROW_HEIGHT / 2 >= totalScrollHeight) {
                    lastDataPiece?.id && debouncedOnFetchMoreSizePacks?.(lastDataPiece.id);
                }
            }
        },
        [rowData, debouncedOnFetchMoreSizePacks]
    );
    return (
        <div
            className="ag-theme-balham sizingGrid"
            {...productCollectionGridViewStyles()}
            data-test={DATATEST.SIZE_PACKS.SIZE_PACK_TABLE}
            ref={containerRef}
        >
            <AgGridReact
                defaultColDef={COLUMN_DEF}
                rowData={rowData}
                columnDefs={columnDef}
                overlayNoRowsTemplate={"<div class='no-rows-node'></div>"}
                suppressContextMenu={true}
                onBodyScroll={handleBodyScroll}
                rowHeight={ROW_HEIGHT}
                suppressScrollOnNewData={true}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
            />
            {!!noRowsNode && createPortal(noRowsComp, noRowsNode)}
        </div>
    );
};
